// M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*
// data-fit LLC · DecisionFlow · TenantAdmin Stage
// RoleAssignmentManager.jsx · DDL: R13 · API: v1.22 · Session 159
// Auth: useAuth() via AuthContext — ARCH-2 compliant
// Key constraints: immutable INSERT role history; self-assignment blocked; revoke is soft, not hard delete
// Stage 12 endpoints wired — api_contract v1.15 §34
// OI-TSA-SHELL-CSS: Full Tailwind rewrite — BEM classes removed, shared primitives adopted

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Ban, Plus, ShieldCheck, X } from 'lucide-react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { apiGet, apiPost, apiDelete } from '../../api/client.js'
import Button from '../shared/Button.jsx'
import Card from '../shared/Card.jsx'
import Pill from '../shared/Pill.jsx'
import { SCREEN_ORDER } from './TenantAdminShell'

const ROLE_NAME_OPTIONS = [
  'tenant_super_admin',
  'comp_admin',
  'finance',
  'manager',
  'rep',
  'tool_admin',
]

const ROLE_NAME_LABELS = {
  tenant_super_admin: 'Tenant Super Admin',
  comp_admin:         'Comp Admin',
  finance:            'Finance',
  manager:            'Manager',
  rep:                'Rep',
  tool_admin:         'Tool Admin',
  platform_admin:     'Platform Admin',
}

const EMPTY_FILTERS     = { user_id: '', userPickerLabel: '' }
const EMPTY_ASSIGN_FORM = {
  user_id:          '',
  userPickerLabel:  '',
  role_name:        'comp_admin',
  business_unit_id: '',
  segment_id:       '',
  effective_date:   '',
  expiry_date:      '',
}

const ERROR_MESSAGES = {
  SELF_ASSIGNMENT_BLOCKED:
    'Self-assignment blocked. Tenant super admins cannot assign themselves roles or revoke their own tenant_super_admin role.',
  AUTH_INSUFFICIENT_ROLE:
    'You do not have sufficient permissions to manage role assignments.',
  ASSIGNMENT_NOT_FOUND: 'Role assignment not found.',
  DATE_RANGE_INVALID:   'Expiry date must be later than effective date.',
  GENERIC:              'Unable to complete the role assignment request.',
}

// ---------------------------------------------------------------------------
// Shared input classes
// ---------------------------------------------------------------------------

const INPUT_CLS = `w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm placeholder-[rgb(var(--color-text-muted))] focus:outline-none disabled:opacity-50`

const SELECT_CLS = `w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none disabled:opacity-50`

const LABEL_CLS = `block text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-1`

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseApiError(error) {
  if (error && typeof error === 'object') {
    const { code, message } = error
    return { code: code || 'UNKNOWN_ERROR', message: message || ERROR_MESSAGES.GENERIC }
  }
  return { code: 'UNKNOWN_ERROR', message: ERROR_MESSAGES.GENERIC }
}

function roleLabel(roleName) {
  return ROLE_NAME_LABELS[roleName] || roleName || 'Unknown role'
}

function buildAssignmentQuery({ cursor, filters }) {
  const params = new URLSearchParams()
  params.set('limit', '50')
  if (cursor) params.set('cursor', cursor)
  if (filters?.user_id?.trim()) params.set('user_id', filters.user_id.trim())
  return params.toString()
}

function isPresent(value) {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

function nullableInteger(value) {
  if (!isPresent(value)) return undefined
  return Number(value)
}

// ---------------------------------------------------------------------------
// UserPicker
// ---------------------------------------------------------------------------

function UserPicker({ value, label, onChange, onSearch, disabled }) {
  const [query, setQuery]       = useState(label || '')
  const [results, setResults]   = useState([])
  const [open, setOpen]         = useState(false)
  const [searching, setSearching] = useState(false)
  const debounceRef = useRef(null)
  const wrapperRef  = useRef(null)

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Sync label if cleared externally
  useEffect(() => {
    if (!value) setQuery('')
  }, [value])

  function handleInput(e) {
    const q = e.target.value
    setQuery(q)
    if (!q.trim()) {
      setResults([])
      setOpen(false)
      onChange('', '')
      if (onSearch) { clearTimeout(debounceRef.current); debounceRef.current = setTimeout(() => onSearch(''), 300) }
      return
    }
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const payload = await apiGet(`/api/v1/users?search=${encodeURIComponent(q.trim())}&sort_by=full_name&sort_dir=asc&limit=20`)
        const users = Array.isArray(payload?.data) ? payload.data : []
        setResults(users)
        setOpen(users.length > 0)
      } catch { setResults([]) } finally { setSearching(false) }
      if (onSearch) onSearch(q)
    }, 300)
  }

  function handleSelect(user) {
    const lbl = `${user.full_name || ''} · ${user.email || ''}`
    setQuery(lbl)
    setResults([])
    setOpen(false)
    onChange(user.user_id, lbl)
    if (onSearch) onSearch(lbl, user.user_id)
  }

  function handleClear() {
    setQuery('')
    setResults([])
    setOpen(false)
    onChange('', '')
    if (onSearch) onSearch('', null)
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex gap-1">
        <input
          type="text"
          className={INPUT_CLS}
          value={query}
          onChange={handleInput}
          placeholder="Search by name or email"
          disabled={disabled}
          autoComplete="off"
        />
        {value && (
          <button type="button" onClick={handleClear} disabled={disabled}
            className="shrink-0 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] ">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {searching && (
        <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Searching…</p>
      )}
      {open && results.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-lg max-h-52 overflow-y-auto">
          {results.map((u) => (
            <li key={u.user_id}>
              <button type="button" onMouseDown={() => handleSelect(u)}
                className="w-full px-3 py-2 text-left text-tok-sm hover:bg-[rgb(var(--color-surface-muted))] ">
                <span className="font-medium text-[rgb(var(--color-text))] ">{u.full_name || '—'}</span>
                <span className="ml-2 text-[rgb(var(--color-text-muted))] ">{u.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// RoleAssignmentManager
// ---------------------------------------------------------------------------

export default function RoleAssignmentManager({
  aiStatus,
  aiResponse,
  currentScreen,
  onNavigate,
}) {
  const { user } = useAuth()

  const [assignments, setAssignments]       = useState([])
  const [nextCursor, setNextCursor]         = useState(null)
  const [hasMore, setHasMore]               = useState(false)
  const [isLoadingMore, setIsLoadingMore]   = useState(false)

  const [filters, setFilters]               = useState(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS)
  const [assignForm, setAssignForm]         = useState(EMPTY_ASSIGN_FORM)
  const [confirmAction, setConfirmAction]   = useState(null)

  const [error, setError]                   = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [loading, setLoading]               = useState(false)
  const [saving, setSaving]                 = useState(false)

  const isCurrentScreen = currentScreen === SCREEN_ORDER[1]

  const loadAssignments = useCallback(
    async ({ cursor = null, filterValues = EMPTY_FILTERS, append = false } = {}) => {
      if (append) setIsLoadingMore(true)
      else setLoading(true)
      setError(null)

      try {
        const queryString = buildAssignmentQuery({ cursor, filters: filterValues })
        // WIRED: GET /api/v1/user-role-assignments — v1.15 §34.1
        const payload = await apiGet(`/api/v1/user-role-assignments?${queryString}`)

        const data = Array.isArray(payload?.data) ? payload.data : []
        const pagination = payload?.pagination || {}
        setAssignments((prev) => (append ? [...prev, ...data] : data))
        setNextCursor(pagination.next_cursor || null)
        setHasMore(Boolean(pagination.has_more))
      } catch (caughtError) {
        const { code, message } = parseApiError(caughtError)
        setError(code === 'AUTH_INSUFFICIENT_ROLE' ? ERROR_MESSAGES.AUTH_INSUFFICIENT_ROLE : message)
      } finally {
        setLoading(false)
        setIsLoadingMore(false)
      }
    },
    [],
  )

  useEffect(() => {
    if (!isCurrentScreen) return
    loadAssignments({ cursor: null, filterValues: appliedFilters, append: false })
  }, [appliedFilters, isCurrentScreen, loadAssignments])

  function updateFilter(fieldName, value) {
    setFilters((current) => ({ ...current, [fieldName]: value }))
  }

  function applyFilters() {
    setSuccessMessage(null)
    setNextCursor(null)
    setAssignments([])
    setAppliedFilters(filters)
  }

  function clearFilters() {
    setSuccessMessage(null)
    setFilters(EMPTY_FILTERS)
    setNextCursor(null)
    setAssignments([])
    setAppliedFilters(EMPTY_FILTERS)
  }

  function updateAssignForm(fieldName, value) {
    setAssignForm((current) => ({ ...current, [fieldName]: value }))
  }

  function validateEffectiveDates() {
    if (!assignForm.expiry_date || !assignForm.effective_date) return true
    return new Date(assignForm.expiry_date) > new Date(assignForm.effective_date)
  }

  function isSelfTarget(targetUserId) {
    return Boolean(targetUserId && user?.sub && targetUserId === user.sub)
  }

  async function assignRole() {
    setError(null)
    setSuccessMessage(null)

    if (isSelfTarget(assignForm.user_id.trim())) {
      setError(ERROR_MESSAGES.SELF_ASSIGNMENT_BLOCKED)
      return
    }
    if (!validateEffectiveDates()) {
      setError(ERROR_MESSAGES.DATE_RANGE_INVALID)
      return
    }

    setSaving(true)

    try {
      const body = {
        user_id:   assignForm.user_id.trim(),
        role_name: assignForm.role_name,
      }

      const businessUnitId = nullableInteger(assignForm.business_unit_id)
      const segmentId      = nullableInteger(assignForm.segment_id)
      if (businessUnitId !== undefined) body.business_unit_id = businessUnitId
      if (segmentId !== undefined)      body.segment_id       = segmentId
      if (assignForm.effective_date)    body.effective_date   = assignForm.effective_date
      if (assignForm.expiry_date)       body.expiry_date      = assignForm.expiry_date

      // WIRED: POST /api/v1/user-role-assignments — v1.15 §34.2
      await apiPost('/api/v1/user-role-assignments', body)

      setAssignForm(EMPTY_ASSIGN_FORM)
      setSuccessMessage('Role assignment created. Existing assignment history remains visible and unchanged.')
      setNextCursor(null)
      setAssignments([])
      await loadAssignments({ cursor: null, filterValues: appliedFilters, append: false })
    } catch (caughtError) {
      const { code, message } = parseApiError(caughtError)
      if (code === 'SELF_ASSIGNMENT_BLOCKED') setError(ERROR_MESSAGES.SELF_ASSIGNMENT_BLOCKED)
      else if (code === 'AUTH_INSUFFICIENT_ROLE') setError(ERROR_MESSAGES.AUTH_INSUFFICIENT_ROLE)
      else setError(message)
    } finally {
      setSaving(false)
    }
  }

  function requestRevoke(assignment) {
    setError(null)
    setSuccessMessage(null)
    if (isSelfTarget(assignment.user_id)) { setError(ERROR_MESSAGES.SELF_ASSIGNMENT_BLOCKED); return }
    setConfirmAction({
      type: 'revoke-role',
      assignment,
      message: 'Revoke this role assignment? This cannot be undone. The historical row remains visible.',
    })
  }

  async function revokeAssignment() {
    if (!confirmAction?.assignment?.assignment_id) return
    const targetAssignment = confirmAction.assignment

    if (isSelfTarget(targetAssignment.user_id)) {
      setError(ERROR_MESSAGES.SELF_ASSIGNMENT_BLOCKED)
      setConfirmAction(null)
      return
    }

    setSaving(true)
    setError(null)
    setSuccessMessage(null)

    try {
      // WIRED: DELETE /api/v1/user-role-assignments/{assignment_id} — v1.15 §34.3
      const payload = await apiDelete(
        `/api/v1/user-role-assignments/${targetAssignment.assignment_id}`,
      )

      const revokedAt = payload?.revoked_at || new Date().toISOString()
      setAssignments((current) =>
        current.map((a) =>
          a.assignment_id === targetAssignment.assignment_id
            ? { ...a, revoked_at: revokedAt, is_active: false }
            : a,
        ),
      )
      setConfirmAction(null)
      setSuccessMessage('Role assignment revoked. The historical row remains visible.')
    } catch (caughtError) {
      const { code, message } = parseApiError(caughtError)
      if (code === 'SELF_ASSIGNMENT_BLOCKED') setError(ERROR_MESSAGES.SELF_ASSIGNMENT_BLOCKED)
      else if (code === 'AUTH_INSUFFICIENT_ROLE') setError(ERROR_MESSAGES.AUTH_INSUFFICIENT_ROLE)
      else if (code === '404' || code === 'ASSIGNMENT_NOT_FOUND') setError(ERROR_MESSAGES.ASSIGNMENT_NOT_FOUND)
      else setError(message)
    } finally {
      setSaving(false)
    }
  }

  const canSubmitAssignment = useMemo(() => (
    assignForm.user_id.trim().length > 0 &&
    ROLE_NAME_OPTIONS.includes(assignForm.role_name)
  ), [assignForm])

  return (
    <div className="space-y-4">
      {/* Screen header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))] ">TA-02</p>
          <h2 className="mt-0.5 text-tok-lg font-bold text-[rgb(var(--color-text))] ">Role Assignment Manager</h2>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))] ">
            Assign additive user roles and revoke assignments without deleting role history.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="secondary" onClick={() => onNavigate(SCREEN_ORDER[0])}>
            ← Users
          </Button>
          <Button variant="secondary" onClick={() => onNavigate(SCREEN_ORDER[2])}>
            Access Scope →
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-start gap-2 rounded-xl bg-[rgb(var(--color-danger))]/10 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))] " role="alert">
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError(null)} className="shrink-0 opacity-60 hover:opacity-100"><X className="h-4 w-4" /></button>
        </div>
      )}
      {successMessage && (
        <div className="flex items-start gap-2 rounded-xl bg-[rgb(var(--color-success))]/10 px-4 py-3 text-tok-sm text-[rgb(var(--color-success))] " role="status">
          <span className="flex-1">{successMessage}</span>
          <button type="button" onClick={() => setSuccessMessage(null)} className="shrink-0 opacity-60 hover:opacity-100"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Confirm revoke dialog */}
      {confirmAction && (
        <div className="rounded-2xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 p-4 " role="alertdialog" aria-modal="false">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-warning))] ">Confirm Revoke</h3>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-warning))] ">{confirmAction.message}</p>
          <div className="mt-3 flex gap-2">
            <Button variant="danger" onClick={revokeAssignment} disabled={saving}>
              <Ban className="h-4 w-4" />
              {saving ? 'Revoking...' : 'Confirm Revoke'}
            </Button>
            <Button variant="secondary" onClick={() => setConfirmAction(null)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Filter */}
      <Card title="Filter Assignments">
        <p className="mb-3 text-tok-xs text-[rgb(var(--color-text-muted))] ">Blank user_id is omitted from the request query string.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <label className={LABEL_CLS}>User</label>
            <UserPicker
              value={filters.user_id}
              label={filters.userPickerLabel}
              onChange={(uid, lbl) => setFilters((f) => ({ ...f, user_id: uid, userPickerLabel: lbl }))}
              onSearch={(_val, resolvedUid) => {
                setFilters((f) => {
                  const next = resolvedUid !== undefined
                    ? { ...f, user_id: resolvedUid }
                    : { ...f, user_id: '', userPickerLabel: '' }
                  setNextCursor(null)
                  setAssignments([])
                  setAppliedFilters(next)
                  return next
                })
              }}
              disabled={loading || saving}
            />
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={applyFilters} disabled={loading || saving}>Apply</Button>
            <Button variant="secondary" onClick={clearFilters} disabled={loading || saving}>Clear</Button>
          </div>
        </div>
      </Card>

      {/* Assign role */}
      <Card
        title="Assign Role"
        action={<ShieldCheck className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />}
      >
        <p className="mb-3 text-tok-xs text-[rgb(var(--color-text-muted))] ">
          Assignments are immutable inserts. New role changes create new rows; old and revoked assignments remain visible.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className={LABEL_CLS}>User</label>
            <UserPicker
              value={assignForm.user_id}
              label={assignForm.userPickerLabel}
              onChange={(uid, lbl) => setAssignForm((f) => ({ ...f, user_id: uid, userPickerLabel: lbl }))}
              disabled={saving}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Role name</label>
            <select className={SELECT_CLS} value={assignForm.role_name} onChange={(e) => updateAssignForm('role_name', e.target.value)}>
              {ROLE_NAME_OPTIONS.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Business unit ID <span className="text-[rgb(var(--color-text-muted))]">(optional)</span></label>
            <input
              type="number"
              className={INPUT_CLS}
              value={assignForm.business_unit_id}
              onChange={(e) => updateAssignForm('business_unit_id', e.target.value)}
              placeholder="Integer"
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Segment ID <span className="text-[rgb(var(--color-text-muted))]">(optional)</span></label>
            <input
              type="number"
              className={INPUT_CLS}
              value={assignForm.segment_id}
              onChange={(e) => updateAssignForm('segment_id', e.target.value)}
              placeholder="Integer"
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Effective date <span className="text-[rgb(var(--color-text-muted))]">(optional)</span></label>
            <input
              type="date"
              className={INPUT_CLS}
              value={assignForm.effective_date}
              onChange={(e) => updateAssignForm('effective_date', e.target.value)}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Expiry date <span className="text-[rgb(var(--color-text-muted))]">(optional)</span></label>
            <input
              type="date"
              className={INPUT_CLS}
              value={assignForm.expiry_date}
              onChange={(e) => updateAssignForm('expiry_date', e.target.value)}
            />
          </div>
        </div>
        <div className="mt-3">
          <Button onClick={assignRole} disabled={!canSubmitAssignment || saving}>
            <Plus className="h-4 w-4" />
            {saving ? 'Assigning...' : 'Assign Role'}
          </Button>
        </div>
      </Card>

      {/* Assignment history */}
      <Card title="Assignment History">
        <p className="mb-3 text-tok-xs text-[rgb(var(--color-text-muted))] ">Revoked assignments remain visible as inactive historical rows.</p>

        {loading && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-10 rounded-xl " />
            ))}
          </div>
        )}

        {!loading && assignments.length === 0 && (
          <p className="py-6 text-center text-tok-sm text-[rgb(var(--color-text-muted))] ">No role assignments found.</p>
        )}

        {!loading && assignments.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-tok-sm">
              <thead>
                <tr className="border-b border-[rgb(var(--color-border))] ">
                  {['User', 'Role', 'Business Unit', 'Segment', 'Effective', 'Expiry', 'Status', 'Revoked At', 'Actions'].map((h) => (
                    <th key={h} className="pb-2 pr-4 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--color-border))] ">
                {assignments.map((assignment) => {
                  const isRevoked = Boolean(assignment.revoked_at || assignment.is_active === false)
                  return (
                    <tr key={assignment.assignment_id} className={isRevoked ? 'opacity-50' : ''}>
                      <td className="py-2.5 pr-4 text-tok-xs text-[rgb(var(--color-text))] ">
                        <span className="font-medium">{assignment.full_name || assignment.email || '—'}</span>
                        {assignment.full_name && assignment.email && (
                          <span className="block text-[rgb(var(--color-text-muted))] ">{assignment.email}</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4 font-medium text-[rgb(var(--color-text))] whitespace-nowrap">{roleLabel(assignment.role_name)}</td>
                      <td className="py-2.5 pr-4 text-[rgb(var(--color-text))] ">{assignment.business_unit_id ?? 'All BUs'}</td>
                      <td className="py-2.5 pr-4 text-[rgb(var(--color-text))] ">{assignment.segment_id ?? 'All'}</td>
                      <td className="py-2.5 pr-4 text-[rgb(var(--color-text))] whitespace-nowrap">{assignment.effective_date || '—'}</td>
                      <td className="py-2.5 pr-4 text-[rgb(var(--color-text))] whitespace-nowrap">{assignment.expiry_date || 'No expiry'}</td>
                      <td className="py-2.5 pr-4">
                        <Pill tone={isRevoked ? 'Blocked' : 'Complete'}>
                          {isRevoked ? 'Revoked' : 'Active'}
                        </Pill>
                      </td>
                      <td className="py-2.5 pr-4 text-[rgb(var(--color-text-muted))] whitespace-nowrap">{assignment.revoked_at || '—'}</td>
                      <td className="py-2.5">
                        <Button
                          variant="ghost"
                          onClick={() => requestRevoke(assignment)}
                          disabled={saving || isRevoked}
                        >
                          <Ban className="h-3.5 w-3.5" />
                          Revoke
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {hasMore && (
          <div className="mt-4">
            <Button
              variant="secondary"
              onClick={() => loadAssignments({ cursor: nextCursor, filterValues: appliedFilters, append: true })}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? 'Loading...' : 'Load More'}
            </Button>
          </div>
        )}
      </Card>
    </div>
  )
}
